import {Trans} from '@lingui/macro';
import React, {FC, useCallback, useMemo} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined, OrNull} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {DropdownBoxCoordinator} from '../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {getChannelIconName} from '../../../helpers/channelViewHelpers';
import {isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {ChannelModel} from '../../../models/api/channelModel';
import {getFlatChannels} from '../../../store/data/channels/channelsSelectors';
import {
  SignatureChannelDropdownInputItem,
  SignaturesChannelDropdown,
  SignaturesChannelDropdownInheritableProps,
} from './signaturesChannelDropdown';
import {SignaturesChannelDropdownValue} from './signaturesChannelDropdownValue';

/*
 * Style.
 */

const StyledDropdownBoxWrapperDiv = styled.div`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
`;

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  padding: 0;
`;

/*
 * Props.
 */

export interface LikeId {
  id: number;
}

export type SelectedDropdownItem<T extends LikeId> = T['id'];

interface SignatureChannelDropdownContainerProps {
  namespace: string;
  inputItems: ReadonlyArray<SignatureChannelDropdownInputItem>;
  selectedItems: OrNull<ReadonlyArray<SelectedDropdownItem<ChannelModel>>>;
  onChange: HandlerOf<OrNull<ReadonlyArray<SelectedDropdownItem<ChannelModel>>>>;
}

/*
 * Component.
 */

export const SignatureChannelDropdownContainer: FC<SignatureChannelDropdownContainerProps> = ({
  namespace,
  ...props
}) => {
  const channels = useChannels(namespace);

  const onChange = (selectedItems: ReadonlyArray<ChannelModel>) => {
    const selectedItemsIds: ReadonlyArray<number> = selectedItems.map((item) => Number(item.id));

    return props.onChange(selectedItemsIds);
  };
  const {selectedItems} = useResolveSelectedItems(props.selectedItems, channels);
  const sharedSignaturesChannelProps: SignaturesChannelDropdownInheritableProps = {
    items: channels,
    getItemKey: (item) => String(item.id),
    renderItemLabel: (item) => item.displayName,
    selectedItems,
    inputItems: props.inputItems,
    renderItemIcon: (item) => <Icon name={getChannelIconName(item.contactSource)} />,
  };

  const renderChannelDropdown = (popoverProps: PopoverTransferProps) => (
    <SignaturesChannelDropdown
      {...sharedSignaturesChannelProps}
      popoverProps={popoverProps}
      serializeItemForSearch={(item) => item.displayName}
      onChange={onChange}
    />
  );

  const renderChannelDropdownValue = () => (
    <SignaturesChannelDropdownValue
      {...sharedSignaturesChannelProps}
      placeholder={<Trans id="signature-editor-select-channel">Select a channel</Trans>}
    />
  );

  return (
    <StyledDropdownBoxWrapperDiv>
      <StyledDropdownBoxCoordinator
        noInputMarginRight
        value={renderChannelDropdownValue()}
        renderDropdown={renderChannelDropdown}
      />
    </StyledDropdownBoxWrapperDiv>
  );
};

/*
 * Helpers.
 */

function checkChannelSupportsSignatures(channel: ChannelModel) {
  return channel && channel.messageType === 'email';
}

function useChannels(namespace: string) {
  const signatureChannels = useAppSelector(getFlatChannels).filter(checkChannelSupportsSignatures);
  if (isTeammateNamespace(namespace)) {
    return signatureChannels;
  }
  return signatureChannels.filter((channel) => channel.namespace === namespace);
}

function useResolveSelectedItems<T extends ChannelModel>(
  selectedItemIds: OrNull<ReadonlyArray<SelectedDropdownItem<T>>>,
  items: ReadonlyArray<T>,
) {
  const resolveSelectedItemIds = useCallback(
    (itemsId: OrNull<ReadonlyArray<SelectedDropdownItem<T>>>) =>
      itemsId?.map((item) => items.find((i) => i.id === item)).filter(isDefined) ?? null,
    [items],
  );

  const selectedItems = useMemo(
    () => resolveSelectedItemIds(selectedItemIds),
    [selectedItemIds, resolveSelectedItemIds],
  );

  return {
    selectedItems,
  };
}
